import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const HeartIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const BagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

export default function Navbar() {
  const { user, logout, isSeller } = useAuth();
  const { cartCount, wishlistCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { label: 'Men', href: '/shop?category=men' },
    { label: 'Women', href: '/shop?category=women' },
    { label: 'Affordable', href: '/affordable' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${scrolled ? 'bg-white/80 backdrop-blur-md shadow-sm' : 'bg-lux-bg'}`}>
        {/* Top announcement bar */}
        <div className="bg-lux-primary text-white text-center py-1.5 transition-all duration-500 overflow-hidden">
          <p className="text-[9px] md:text-xs tracking-[0.3em] uppercase font-sans font-light">Free delivery on orders over ₹1500</p>
        </div>

        <div className="px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/home" className="flex-shrink-0">
              <span className="font-display text-xl font-semibold tracking-[0.15em] text-lux-primary">AURÉNE</span>
            </Link>

            {/* Center nav */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map(link => (
                <Link key={link.label} to={link.href}
                  className="label-sm text-lux-secondary hover:text-lux-primary transition-colors duration-200 relative group">
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-lux-gold transition-all duration-300 group-hover:w-full" />
                </Link>
              ))}
              {user && (
                <Link to="/design-editor"
                  className="label-sm text-lux-secondary hover:text-lux-primary transition-colors duration-200 relative group">
                  Design Editor
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-lux-gold transition-all duration-300 group-hover:w-full" />
                </Link>
              )}
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-5">
              <button onClick={() => setSearchOpen(true)} className="text-lux-secondary hover:text-lux-primary transition-colors">
                <SearchIcon />
              </button>

              {user && (
                <Link to="/wishlist" className="relative text-lux-secondary hover:text-lux-primary transition-colors">
                  <HeartIcon />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-4 h-4 bg-lux-gold text-white text-xs rounded-full flex items-center justify-center font-sans">
                      {wishlistCount}
                    </span>
                  )}
                </Link>
              )}

              <Link to="/cart" className="relative text-lux-secondary hover:text-lux-primary transition-colors">
                <BagIcon />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-4 h-4 bg-lux-primary text-white text-xs rounded-full flex items-center justify-center font-sans">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Profile Dropdown */}
              {user ? (
                <div className="relative" ref={profileRef}>
                  <button onClick={() => setProfileOpen(!profileOpen)} className="text-lux-secondary hover:text-lux-primary transition-colors flex items-center gap-1">
                    <UserIcon />
                  </button>
                  {profileOpen && (
                    <div className="absolute right-0 top-8 w-52 bg-white border border-lux-border shadow-lg z-50 animate-fade-in">
                      <div className="px-4 py-3 border-b border-lux-border">
                        <p className="font-sans text-xs font-medium text-lux-primary">{user.firstName} {user.lastName}</p>
                        <p className="font-sans text-xs text-lux-secondary truncate">{user.email}</p>
                        <span className="inline-block mt-1 text-xs text-lux-gold tracking-wider uppercase">{user.role}</span>
                      </div>
                      {isSeller ? (
                        <Link to="/seller/dashboard" className="block px-4 py-2.5 text-xs font-sans text-lux-primary hover:bg-lux-bg transition-colors" onClick={() => setProfileOpen(false)}>
                          {user.role === 'main_admin' ? 'Admin Dashboard' : user.role === 'local_admin' ? 'Seller Dashboard' : 'Seller Dashboard'}
                        </Link>
                      ) : (
                        <>
                          <Link to="/orders" className="block px-4 py-2.5 text-xs font-sans text-lux-primary hover:bg-lux-bg transition-colors" onClick={() => setProfileOpen(false)}>My Orders</Link>
                          <Link to="/wishlist" className="block px-4 py-2.5 text-xs font-sans text-lux-primary hover:bg-lux-bg transition-colors" onClick={() => setProfileOpen(false)}>Wishlist</Link>
                          <Link to="/my-designs" className="block px-4 py-2.5 text-xs font-sans text-lux-primary hover:bg-lux-bg transition-colors" onClick={() => setProfileOpen(false)}>My Designs</Link>
                          <Link to="/design-editor" className="block px-4 py-2.5 text-xs font-sans text-lux-primary hover:bg-lux-bg transition-colors" onClick={() => setProfileOpen(false)}>Design Editor</Link>
                        </>
                      )}
                      <button onClick={() => { logout(); setProfileOpen(false); navigate('/'); }}
                        className="w-full text-left px-4 py-2.5 text-xs font-sans text-lux-secondary hover:text-lux-primary hover:bg-lux-bg transition-colors border-t border-lux-border">
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="text-lux-secondary hover:text-lux-primary transition-colors">
                  <UserIcon />
                </Link>
              )}

              {/* Mobile menu button */}
              <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-lux-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  {menuOpen ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-lux-bg border-t border-lux-border px-6 py-4 animate-fade-in">
            {navLinks.map(link => (
              <Link key={link.label} to={link.href} className="block py-3 label-sm text-lux-secondary hover:text-lux-primary border-b border-lux-border" onClick={() => setMenuOpen(false)}>
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center pt-24 animate-fade-in" onClick={() => setSearchOpen(false)}>
          <div className="bg-white w-full max-w-2xl mx-4 p-8 shadow-2xl" onClick={e => e.stopPropagation()}>
            <p className="label-sm text-lux-secondary mb-6">Search AURÉNE</p>
            <form onSubmit={handleSearch} className="flex items-center gap-4">
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search for products, brands..."
                className="flex-1 border-b border-lux-primary bg-transparent py-2 text-lg font-display font-light text-lux-primary placeholder:text-lux-border focus:outline-none"
              />
              <button type="submit" className="btn-primary py-2 px-6">Search</button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
